import { Nullable } from "@core/domain/types/nullable.type";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { CreateFinancialEntityBudget } from "@project/domain/models/budget/create-financial-entity-budget.model";
import { FinancialEntityBudget } from "@project/domain/models/budget/financial-entity-budget.model";
import { CreateBudgetUseCase } from "@project/domain/usecases/budget/create-budget-usecase";
import { FetchJustificationTemplatesUseCase } from "@project/domain/usecases/budget/fetch-justification-templates-usecase";
import { FinancialEntityBudgetFormValuesValidated } from "@project/presentation/components/budget-form/budget-form.component";
import { inject, injectable } from "inversify";
import { makeObservable, runInAction } from "mobx";

export interface JustificationTemplateOption {
    id: number;
    name: string;
}

@injectable()
export class CreateBudgetTemplatePageViewModel extends BaseViewModel {
    private isTemplateMode = false;

    justificationTemplates: JustificationTemplateOption[] = [];
    loadingJustificationTemplates = false;

    constructor(
        @inject(CreateBudgetUseCase)
        private readonly createBudgetUseCase: CreateBudgetUseCase,
        @inject(FetchJustificationTemplatesUseCase)
        private readonly fetchJustificationTemplatesUseCase: FetchJustificationTemplatesUseCase,
    ) {
        super();
        makeObservable(this, {
            justificationTemplates: true,
            loadingJustificationTemplates: true,
            fetchJustificationTemplates: false,
        });
    }

    setTemplateMode(isTemplate: boolean): void {
        this.isTemplateMode = isTemplate;
    }

    async fetchJustificationTemplates(): Promise<void> {
        this.loadingJustificationTemplates = true;
        try {
            const templates = await this.fetchJustificationTemplatesUseCase.execute();
            runInAction(() => {
                this.justificationTemplates = templates;
            });
        } finally {
            runInAction(() => {
                this.loadingJustificationTemplates = false;
            });
        }
    }

    async createBudget(
        budget: FinancialEntityBudgetFormValuesValidated,
    ): Promise<Nullable<FinancialEntityBudget>> {
        LoadLayoutStore.start();

        const createBudget = new CreateFinancialEntityBudget(
            budget.name,
            budget.description,
            this.isTemplateMode ? null : budget.financialEntityId || null,
            budget.justificationTemplateId,
            this.isTemplateMode
        );

        const result = await this.createBudgetUseCase.execute(createBudget);

        if (result) {
            ToastManagerStore.success();
        }

        LoadLayoutStore.finish();

        return result;
    }
}
